import { http, httpInterceptor, track as trackHttpNetwork, showDialog } from 'cdui-js';
import { trackApiRequest, trackApiResponse, type ApiRequestAnalyticsCtx } from './analytics';
import {
  beginAccountOpenApiTracking,
  finishAccountOpenApiTracking,
  type AccountOpenApiCtx,
} from './analytics/events/accountOpenApi';
import { userState, isH5User } from './stores/user';
import { showToast, resolveToastMessage } from './pages/mobile/components/Toast';
import { getLang, serviceLang } from './i18n';
import { consoleLog } from './lib/utils';
import { getActivityAuthorization, notifyActivityTokenExpired } from './activity/shared/activitySession';
import { getOpenAccountAuthorization, notifyOpenAccountTokenExpired, isOpenAccountAppWebView } from './pages/mobile/account/open-progress/openAccountSession';
import { getAppRequestDevice } from './lib/appRequestDevice';

/** 登录二次验证业务码（对齐 App HTTP_BIZ_LOGIN_QUADRATIC_VERIFY） */
export const HTTP_BIZ_LOGIN_QUADRATIC_VERIFY = 10015;

// 服务端渲染错误信息集合
export const SSR_ERRORS = [];

type RequestInitWithAnalytics = RequestInit & {
  __analytics?: ApiRequestAnalyticsCtx;
  __accountOpenApi?: AccountOpenApiCtx;
  /** 开户 App WebView：这笔请求发出时还没有 Bootstrap authorization */
  __openAccountSentWithoutAuth?: boolean;
};

function newAnalyticsId(prefix: string): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function resolveApiPath(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    if (/^[a-z]+:/i.test(raw)) {
      const u = new URL(raw);
      return u.pathname || raw.split('?')[0];
    }
  } catch {
    /* fall through */
  }
  return raw.split('?')[0];
}

function toFiniteNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** 业务自己处理失败时带上，拦截器不再弹 message */
function requestSilent(options?: RequestInit): boolean {
  const headers = options?.headers;
  if (!headers) return false;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return headers.get('X-No-Toast') === '1';
  }
  if (Array.isArray(headers)) {
    const hit = headers.find((pair) => String(pair[0]).toLowerCase() === 'x-no-toast');
    return hit ? String(hit[1]) === '1' : false;
  }
  const record = headers as Record<string, string>;
  return record['X-No-Toast'] === '1' || record['x-no-toast'] === '1';
}

function reportApiResponse(
  options: RequestInitWithAnalytics | undefined,
  httpCode: number | string,
  bizCode: number | string,
): void {
  const ctx = options?.__analytics;
  if (!ctx?.trace_id) return;
  trackApiResponse({
    trace_id: ctx.trace_id,
    request_id: ctx.request_id,
    api_path: ctx.api_path,
    http_code: httpCode,
    biz_code: bizCode,
    duration_ms: Date.now() - ctx.start_ts,
  });
}

function reportAccountOpenApiResponse(
  options: RequestInitWithAnalytics | undefined,
  result: {
    is_success: boolean;
    http_code?: number;
    biz_code?: number | string;
    response_body?: unknown;
    is_network_error?: boolean;
    error_message?: string;
  },
): void {
  finishAccountOpenApiTracking(options, {
    is_success: result.is_success,
    http_code: result.http_code,
    biz_code: toFiniteNumber(result.biz_code),
    response_body: result.response_body,
    trace_id: options?.__analytics?.trace_id,
    is_network_error: result.is_network_error,
    error_message: result.error_message,
  });
}

/** H5：403/430（如已登录到其它设备）清登录态并打开登录页 */
const redirectH5ToLogin = () => {
  if (!isH5User()) return;
  localStorage.removeItem('token');
  userState.isLogin = false;
  userState.icon = '';
  userState.showLoginPage = true;
};

/**
 * DEV 调试：URL 拼 ?token=xxx（上线清空此数组即可关掉）
 * 匹配业务相对路径（不含 /api/v1）
 */
const DEV_APPEND_TOKEN_PATHS: string[] = [];

function isDevMode() {
  return String(import.meta.env.VITE_MODE || '').replace(/['"]/g, '') === 'dev';
}

function shouldAppendTokenToUrl(path: string) {
  if (!isDevMode() || !DEV_APPEND_TOKEN_PATHS.length) return false;
  const bare = String(path || '').split('?')[0];
  return DEV_APPEND_TOKEN_PATHS.some((p) => bare === p || bare.endsWith(p));
}

function appendTokenQuery(url: string, token: string | null) {
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/** App 开户 session → 活动 session → H5 localStorage；与 get/post 拦截器同源 */
function resolveAuthorization(): string | null {
  const openAccountAuthorization = getOpenAccountAuthorization();
  if (openAccountAuthorization !== undefined) return openAccountAuthorization;
  const activityAuthorization = getActivityAuthorization();
  if (activityAuthorization !== undefined) return activityAuthorization;
  return localStorage.getItem('token');
}

/** 发出时没有 token 的 401 不是 access 失效，不能清 App 会话 */
function sentWithoutOpenAccountAuth(options?: RequestInitWithAnalytics): boolean {
  return options?.__openAccountSentWithoutAuth === true;
}

function noteOpenAccountAuth(options: RequestInitWithAnalytics, authorization: string | null | undefined) {
  if (!isOpenAccountAppWebView()) return;
  options.__openAccountSentWithoutAuth = !String(authorization || '').trim();
}

function notifyOpenAccount401(options: RequestInitWithAnalytics | undefined, reason: 'biz-401' | 'http-401') {
  if (sentWithoutOpenAccountAuth(options)) return;
  notifyOpenAccountTokenExpired(reason);
}

function applyCommonRequestHeaders(headers: Record<string, string | null | undefined>) {
  const appDevice = getAppRequestDevice();
  headers['Authorization'] = resolveAuthorization();
  headers['locale'] = serviceLang[getLang()] || 'CH';
  headers['deviceCode'] = appDevice.deviceCode || localStorage.getItem('deviceCode') || 'lzj_pc_test01';
  if (appDevice.deviceModel) headers['deviceModel'] = appDevice.deviceModel;
  if (appDevice.deviceVersion) headers['deviceVersion'] = appDevice.deviceVersion;
  if (appDevice.platform) headers['platform'] = appDevice.platform;
}

// http请求拦截
httpInterceptor.request = (url: string, options: RequestInit) => {
  let headers = options.headers || (options.headers = {});
  applyCommonRequestHeaders(headers as Record<string, string | null | undefined>);
  // deviceCode：lzj_pc_test01

  const api_path = resolveApiPath(url);
  const trace_id = newAnalyticsId('trace');
  const request_id = newAnalyticsId('req');
  headers['X-Trace-Id'] = trace_id;
  headers['X-Request-Id'] = request_id;
  (options as RequestInitWithAnalytics).__analytics = {
    api_path,
    trace_id,
    request_id,
    start_ts: Date.now(),
  };
  trackApiRequest({ api_path, trace_id, request_id });
  beginAccountOpenApiTracking(url, options as RequestInitWithAnalytics, { request_id, trace_id });

  let finalUrl =
    import.meta.env.MODE === 'development'
      ? import.meta.env.VITE_API_BASE_URL + url
      : url && !/^[a-z]+:/.test(url)
        ? import.meta.env.VITE_API_BASE_URL + url
        : url;

  const auth =
    (headers as Record<string, string | undefined>)['Authorization'] ||
    (headers as Record<string, string | undefined>)['authorization'] ||
    null;
  noteOpenAccountAuth(options as RequestInitWithAnalytics, auth);

  // 仅 DEV + 白名单：URL 带 token，原有 query 保留
  if (shouldAppendTokenToUrl(url) || shouldAppendTokenToUrl(finalUrl)) {
    finalUrl = appendTokenQuery(finalUrl, auth);
  }

  consoleLog('[H5][HTTP]', { url: finalUrl, hasAuthorization: Boolean(auth) }, 'dev');

  return finalUrl;
};

// http响应拦截
httpInterceptor.response = (response: Response, options?: RequestInit) => {
  const reqOptions = options as RequestInitWithAnalytics | undefined;

  if (response.ok) {
    return response.json().then((result) => {
      console.log('[HTTP Response]', {
        url: response.url,
        status: response.status,
        method: options?.method || 'GET',
        result,
      });

      const bizOk = +result.code === 200;
      reportApiResponse(reqOptions, response.status, result?.code ?? '');
      reportAccountOpenApiResponse(reqOptions, {
        is_success: bizOk,
        http_code: response.status,
        biz_code: result?.code,
        response_body: result,
        error_message: bizOk ? undefined : String(result?.message || ''),
      });

      if (!bizOk) {
        // 登录二次验证：不当失败，不 toast，把内层 data 交给业务（见 docs/h5-login-quadratic-verify.md）
        if (+result.code === HTTP_BIZ_LOGIN_QUADRATIC_VERIFY) {
          return Promise.reject({
            code: HTTP_BIZ_LOGIN_QUADRATIC_VERIFY,
            message: result.message,
            data: result.data,
          });
        }

        if (+result.code === 401) {
          if (isOpenAccountAppWebView()) {
            notifyOpenAccount401(reqOptions, 'biz-401');
          } else {
            notifyActivityTokenExpired('biz-401');
          }
        } else if (+result.code === 403 || +result.code === 430) {
          showToast({ message: resolveToastMessage(result.message), duration: 3000 });
          redirectH5ToLogin();
        } else if (!requestSilent(reqOptions) && !result.message?.includes('已登录')) {
          showToast({ message: resolveToastMessage(result.message), duration: 3000 });
        }

        return Promise.reject(result.code + ' ' + result.message);
      }

      return {
        type: 'custom',
        result: result.data,
      };
    });
  }

  // HTTP 非 2xx
  reportApiResponse(reqOptions, response.status, `http_${response.status}`);
  reportAccountOpenApiResponse(reqOptions, {
    is_success: false,
    http_code: response.status,
    error_message: `${response.status} ${response.statusText || ''}`.trim(),
  });

  // 活动 App WebView 没有 H5 登录页，401 交给 Native 统一清会话。
  // 发出时没有 token（Bootstrap 尚未写入）只拒绝本请求，不清 App 登录态。
  if (response.status === 401) {
    if (isOpenAccountAppWebView()) {
      notifyOpenAccount401(reqOptions, 'http-401');
    } else {
      notifyActivityTokenExpired('http-401');
    }
    return Promise.reject(response.status + ' ' + response.statusText) as any;
  }

  // HTTP 非 2xx：403 时 H5 跳转登录
  if (response.status === 403) {
    redirectH5ToLogin();
    return Promise.reject(response.status + ' ' + response.statusText) as any;
  }
};

/** http.send 不走 request 拦截器：补 Authorization + 开户/加开入参出参埋点（上传 / idAuth FormData） */
const rawHttpSend = http.send.bind(http);
http.send = ((url: string, options?: RequestInit) => {
  const opts = (options || {}) as RequestInitWithAnalytics;
  const headers = (opts.headers || (opts.headers = {})) as Record<string, string | null | undefined>;
  applyCommonRequestHeaders(headers);
  noteOpenAccountAuth(opts, headers['Authorization']);
  // 不挂 __analytics，避免只打 api_response 无 api_request；account_open_* 独立串联
  beginAccountOpenApiTracking(url, opts, { request_id: newAnalyticsId('req') });
  return rawHttpSend(url, opts).catch((error: unknown) => {
    reportAccountOpenApiResponse(opts, {
      is_success: false,
      is_network_error: true,
      error_message: error instanceof Error ? error.message : String(error || 'network_error'),
    });
    return Promise.reject(error);
  });
}) as typeof http.send;

/** get/post 网络层失败（超时等）补 account_open_api_response */
trackHttpNetwork((result) => {
  if (!result.error) return;
  const opts = result.options as RequestInitWithAnalytics | undefined;
  reportAccountOpenApiResponse(opts, {
    is_success: false,
    is_network_error: true,
    error_message:
      result.error instanceof Error
        ? result.error.message
        : String(result.error || 'network_error'),
  });
});

// 错误码统一处理, noToast:是否不提示错误
// async function handleError(error) {
//   const code = error?.data?.code;
//   const message = error?.data?.message;

//   switch (code) {
//     case 401:
//       // Token 过期时,分3种情况，accessToken过期和refreshToken过期, NO_TOKEN_FOUND_IN_REQUEST: 没有cookie(第一次访问)
//       if (message === 'ACCESS_TOKEN_EXPIRED') {
//         // accessToken过期，要调用刷新token接口获取最新的token
//         return await handleExpire(error);
//       } else {
//         // refreshToken过期
//         handleLogout(message !== 'NO_TOKEN_FOUND_IN_REQUEST');
//         return Promise.reject(error);
//       }
//     case 407:
//       // Token 过期时
//       // 顶号
//       handleLogout();
//       return Promise.reject(error);
//     case 18014:
//     case 10085:
//       // 需要设备授权
//       // 需要登录验证
//       return Promise.reject(error.data);
//     default:
//       if (axios.isCancel(error)) {
//         console.log('请求被 signal 取消了');
//         return Promise.reject(error);
//       }

//       // 不是正确的 code
//       if (error.status === 200) {
//         const noToast = error?.config?.noToast || false;
//         !noToast && window.$message?.error(message || 'Error');
//       } else {
//         // 对错误响应进行处理
//         // 请求超时
//         if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
//           // 请求超时
//           window.$message?.error('请求超时，请稍后重试');
//         } else {
//           // 请求发生错误
//           window.$message?.error('服务器错误，请稍后重试');
//         }
//       }

//       return Promise.reject(error);
//   }
// }
